import React, { useState, useEffect, useCallback } from 'react';
import { logger, MediaConfig } from '@musedrops/shared';
import type { Insert, Language } from '@musedrops/shared';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  TextField,
  IconButton,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Plus } from 'lucide-react';
import { LAYOUT } from '../../../theme';
import {
  listInserts,
  createInsert,
  updateInsert,
  deleteInsert,
} from '../../../data/inserts';
import { getAllLanguages } from '../../../data/language';
import { supabase } from '../../../services/supabase';

const SLUG_REGEX = /^[a-z][a-z0-9-]{1,63}$/;

interface DialogState {
  open: boolean;
  mode: 'create' | 'edit';
  originalSlug: string; // For edit mode: the slug before any changes
  slug: string;
  title: string;
  description: string;
  enabled: boolean;
  translated: boolean;
  minStoriesPlayed: number;
}

const emptyDialog: DialogState = {
  open: false,
  mode: 'create',
  originalSlug: '',
  slug: '',
  title: '',
  description: '',
  enabled: true,
  translated: false,
  minStoriesPlayed: 0,
};

/** Build public URL for an insert media file */
function getInsertMediaUrl(slug: string, filename: string): string {
  const { data } = supabase.storage
    .from('media')
    .getPublicUrl(`inserts/${slug}/${filename}`);
  return data.publicUrl;
}

/**
 * Admin settings page for managing inserts.
 * Provides a table of all inserts with inline status chips,
 * and a dialog for creating/editing/deleting inserts with media preview.
 */
export const InsertsSettings: React.FC = () => {
  const [inserts, setInserts] = useState<Insert[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [dialog, setDialog] = useState<DialogState>(emptyDialog);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [insertsData, langsData] = await Promise.all([
        listInserts(),
        getAllLanguages(),
      ]);
      setInserts(insertsData);
      setLanguages(langsData);
    } catch (error) {
      logger.error('Failed to load inserts', { error: String(error) });
      setNotification({ message: 'Failed to load inserts', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Dialog ──

  const openCreateDialog = () => {
    setDialog({ ...emptyDialog, open: true, mode: 'create' });
    setDialogError(null);
  };

  const openEditDialog = (insert: Insert) => {
    setDialog({
      open: true,
      mode: 'edit',
      originalSlug: insert.slug,
      slug: insert.slug,
      title: insert.title,
      description: insert.description,
      enabled: insert.enabled,
      translated: insert.translated,
      minStoriesPlayed: insert.minStoriesPlayed,
    });
    setDialogError(null);
  };

  const closeDialog = () => {
    setDialog(emptyDialog);
    setDialogError(null);
  };

  const validateDialog = (): string | null => {
    if (!SLUG_REGEX.test(dialog.slug)) {
      return 'Slug must be kebab-case (lowercase letters, numbers, hyphens), 2-64 chars';
    }
    if (
      inserts.some(i => i.slug === dialog.slug && i.slug !== dialog.originalSlug)
    ) {
      return `Slug "${dialog.slug}" already exists`;
    }
    if (!dialog.title.trim()) {
      return 'Title is required';
    }
    if (dialog.minStoriesPlayed < 0) {
      return 'Min stories played must be >= 0';
    }
    return null;
  };

  const handleDialogSave = async () => {
    const error = validateDialog();
    if (error) {
      setDialogError(error);
      return;
    }

    try {
      setSaving(true);
      if (dialog.mode === 'create') {
        await createInsert({
          slug: dialog.slug,
          title: dialog.title.trim(),
          description: dialog.description.trim(),
          enabled: dialog.enabled,
          translated: dialog.translated,
          minStoriesPlayed: dialog.minStoriesPlayed,
        });
        setNotification({
          message: `Insert "${dialog.slug}" created`,
          type: 'success',
        });
      } else {
        const updatePayload: Record<string, unknown> = {
          title: dialog.title.trim(),
          description: dialog.description.trim(),
          enabled: dialog.enabled,
          translated: dialog.translated,
          minStoriesPlayed: dialog.minStoriesPlayed,
        };
        // Slug rename: updates the PK. Note that existing inserts_seen entries
        // in user_profiles still reference the old slug, so users who already saw
        // this insert will see it again under the new slug.
        if (dialog.slug !== dialog.originalSlug) {
          updatePayload.slug = dialog.slug;
        }
        await updateInsert(dialog.originalSlug, updatePayload);
        setNotification({
          message: `Insert "${dialog.slug}" updated`,
          type: 'success',
        });
      }
      closeDialog();
      await loadData();
    } catch (err) {
      logger.error('Failed to save insert', { error: String(err) });
      setDialogError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug: string) => {
    try {
      setSaving(true);
      await deleteInsert(slug);
      setNotification({
        message: `Insert "${slug}" deleted`,
        type: 'success',
      });
      setDeleteConfirm(null);
      await loadData();
    } catch (err) {
      logger.error('Failed to delete insert', { error: String(err) });
      setNotification({ message: 'Failed to delete', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Media preview ──

  const renderMediaPreview = (slug: string, translated: boolean) => {
    const imageUrl = getInsertMediaUrl(slug, MediaConfig.insert.coverImage);
    const audioLang = translated ? languages.map(l => l.key) : ['en'];

    return (
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Image ({MediaConfig.insert.coverImage})
          </Typography>
          <Box
            component="img"
            src={imageUrl}
            alt={`${slug} cover`}
            sx={{
              display: 'block',
              maxWidth: 200,
              maxHeight: 200,
              borderRadius: 1,
              mt: 0.5,
              bgcolor: 'grey.100',
            }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.alt = `Not found: inserts/${slug}/${MediaConfig.insert.coverImage}`;
              e.currentTarget.style.padding = '16px';
              e.currentTarget.style.fontSize = '12px';
              e.currentTarget.style.color = '#999';
            }}
          />
        </Box>
        {audioLang.map(lang => {
          const audioFilename = MediaConfig.insert.audio.replace(
            '{lang}',
            lang
          );
          const audioUrl = getInsertMediaUrl(slug, audioFilename);
          return (
            <Box key={lang}>
              <Typography variant="caption" color="text.secondary">
                Audio {translated ? `(${lang})` : ''}
              </Typography>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio controls src={audioUrl} style={{ display: 'block', marginTop: 4 }} />
            </Box>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, maxWidth: LAYOUT.mediumContentWidth, margin: '0 auto' }}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Slug</TableCell>
              <TableCell>Title</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell align="center">Translated</TableCell>
              <TableCell align="center">Min Played</TableCell>
              <TableCell align="center">
                <IconButton
                  onClick={openCreateDialog}
                  color="primary"
                  size="small"
                >
                  <Plus size={18} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inserts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No inserts yet. Click + to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              inserts.map(insert => (
                <TableRow key={insert.slug} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    >
                      {insert.slug}
                    </Typography>
                    {insert.description && (
                      <Typography variant="caption" color="text.secondary">
                        {insert.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{insert.title}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={insert.enabled ? 'Yes' : 'No'}
                      size="small"
                      color={insert.enabled ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={insert.translated ? 'Yes' : 'No'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {insert.minStoriesPlayed}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => openEditDialog(insert)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.mode === 'create' ? 'Add Insert' : `Edit: ${dialog.originalSlug}`}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Slug"
            value={dialog.slug}
            onChange={e =>
              setDialog(d => ({ ...d, slug: e.target.value }))
            }
            fullWidth
            margin="normal"
            helperText="kebab-case (e.g. welcome-testers)"
          />

          <TextField
            label="Title"
            value={dialog.title}
            onChange={e =>
              setDialog(d => ({ ...d, title: e.target.value }))
            }
            fullWidth
            margin="normal"
          />

          <TextField
            label="Description"
            value={dialog.description}
            onChange={e =>
              setDialog(d => ({ ...d, description: e.target.value }))
            }
            fullWidth
            margin="normal"
            helperText="Single line description of this insert"
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mt: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={dialog.enabled}
                onChange={e =>
                  setDialog(d => ({ ...d, enabled: e.target.checked }))
                }
                size="small"
              />
              <Typography variant="body2">Enabled</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={dialog.translated}
                onChange={e =>
                  setDialog(d => ({
                    ...d,
                    translated: e.target.checked,
                  }))
                }
                size="small"
              />
              <Typography variant="body2">Translated</Typography>
            </Box>
          </Box>

          <TextField
            label="Min Stories Played"
            value={dialog.minStoriesPlayed}
            onChange={e =>
              setDialog(d => ({
                ...d,
                minStoriesPlayed: parseInt(e.target.value, 10) || 0,
              }))
            }
            fullWidth
            margin="normal"
            type="number"
            inputProps={{ min: 0, step: 1 }}
            helperText="User must have played at least this many stories to see the insert"
          />

          {/* Media preview */}
          {dialog.slug &&
            renderMediaPreview(dialog.slug, dialog.translated)}

          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {dialog.mode === 'edit' && (
            <Button
              color="error"
              onClick={() => {
                closeDialog();
                setDeleteConfirm(dialog.slug);
              }}
              disabled={saving}
              sx={{ mr: 'auto' }}
            >
              Delete
            </Button>
          )}
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDialogSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
      >
        <DialogTitle>Delete insert?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            disabled={saving}
          >
            {saving ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.type}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
